import { Modal } from '@eventbrite/eds-modal';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { gettext } from '@eventbrite/i18n';
import { has } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import React, { LegacyRef, PureComponent } from 'react';
import {
    Payment,
    PaymentMethod,
    PAYMENT_METHODS_INFO,
    RedirectInstructions,
} from '../models';
import { isWindowLocationRedirectedPaymentMethod } from '../utils';
import rebrandStyles from './Page.rebranding.module.scss';
import { getStripePaymentTypeName } from './utils/getStripePaymentTypeName';

export interface PageProps {
    setLoading: () => void;
    redirectInstructions?: RedirectInstructions;
    payment?: Payment;
    isAppRebrandActive: boolean;
}

const getRedirectMessageText = (payment: Payment) => {
    if (payment.paymentMethod === PaymentMethod.Ideal) {
        return gettext('Redirecting to %(bankName)s', {
            bankName: payment.bankName,
        });
    }

    if (payment.paymentMethod === PaymentMethod.StripeConfirmationToken) {
        const paymentType = getStripePaymentTypeName(payment.stripePaymentType);
        if (paymentType) {
            return gettext('Redirecting to %(provider)s', {
                provider: paymentType,
            });
        }
    }

    if (has(payment.paymentMethod, PAYMENT_METHODS_INFO)) {
        return gettext('Redirecting to %(paymentMethod)s', {
            paymentMethod:
                PAYMENT_METHODS_INFO[
                    payment.paymentMethod
                ].userString.toString(),
        });
    }

    return gettext('Redirecting');
};

const getRedirectMessage = (
    payment?: Payment,
    isAppRebrandActive?: boolean,
) => {
    if (!payment) {
        return null;
    }

    const message = getRedirectMessageText(payment);
    const wrapperClassName = classNames({
        'eds-text-bl': !isAppRebrandActive,
        'eds-l-pad-top-2': isAppRebrandActive,
    });

    return (
        <p className={wrapperClassName} data-spec="redirect-message">
            {message}
        </p>
    );
};

const getRedirectFallbackLink = (
    payment?: Payment,
    redirectUsingWindowLocation?: () => void,
) => {
    if (
        !payment ||
        payment.paymentMethod !== PaymentMethod.StripeConfirmationToken
    ) {
        return null;
    }

    return (
        <p data-spec="redirect-fallback-link" className="eds-text--center">
            <span className="eds-text--grey-700">
                {gettext('If you are not redirected automatically, ')}
            </span>
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    redirectUsingWindowLocation?.();
                }}
            >
                {gettext('click here')}
            </a>
            <br />
            <span className="eds-text--grey-700">
                {gettext('to securely continue to your payment.')}
            </span>
        </p>
    );
};

const getRedirectForm = ({
    redirectInstructions,
    formRef,
}: {
    redirectInstructions?: RedirectInstructions;
    formRef: LegacyRef<HTMLFormElement>;
}) => {
    if (!redirectInstructions) {
        return null;
    }

    const args = redirectInstructions.arguments;

    const inputFieldsForArgs = Object.entries(args).map(([name, value]) => (
        <input type="hidden" key={name} name={name} value={value} />
    ));

    return (
        <div hidden={true} data-spec="redirect-form">
            <form
                method={redirectInstructions.method}
                action={redirectInstructions.url}
                ref={formRef}
            >
                {inputFieldsForArgs}
            </form>
        </div>
    );
};

const redirectUsingWindowLocation = ({
    redirectInstructions,
    payment,
}: {
    redirectInstructions?: RedirectInstructions;
    payment?: Payment;
}) => {
    if (
        !redirectInstructions ||
        !payment ||
        !isWindowLocationRedirectedPaymentMethod(payment)
    ) {
        return;
    }

    const args = redirectInstructions.arguments;
    const redirectUrlWithArgs = `${redirectInstructions.url}?${Object.entries(
        args,
    )
        .map(([name, value]) => `${name}=${value}`)
        .join('&')}`;
    window.location.href = redirectUrlWithArgs;
};

export default class Page extends PureComponent<PageProps> {
    private formRef: LegacyRef<HTMLFormElement>;

    constructor(props: PageProps) {
        super(props);

        this.formRef = async (form) => {
            if (
                this.props.payment &&
                !isWindowLocationRedirectedPaymentMethod(this.props.payment)
            ) {
                form?.submit();
            }
        };
    }

    componentDidMount() {
        this.props.setLoading();
    }

    render() {
        const INLINE_STYLES = {
            container: {
                height: '100%',
            },
        };

        const { redirectInstructions, payment, isAppRebrandActive } =
            this.props;

        redirectUsingWindowLocation({ redirectInstructions, payment });

        const styles = isAppRebrandActive ? rebrandStyles : {};

        const redirectForm = getRedirectForm({
            redirectInstructions,
            formRef: this.formRef,
        });

        const redirectMessage = getRedirectMessage(payment, isAppRebrandActive);

        const redirectFallbackLink = getRedirectFallbackLink(payment, () =>
            redirectUsingWindowLocation({ redirectInstructions, payment }),
        );

        const progressIndicatorStyle = isAppRebrandActive ? 'dark' : 'gradient';

        return (
            <Modal isShown={true} __containerClassName={styles?.wrapper}>
                <div
                    data-spec="progress-indicator"
                    className="eds-align--center eds-text--center"
                    style={INLINE_STYLES.container}
                >
                    <section>
                        <ProgressIndicator
                            style={progressIndicatorStyle}
                            isAppRebrandActive={isAppRebrandActive}
                        />
                        {redirectMessage}
                        {redirectFallbackLink}
                        {redirectForm}
                    </section>
                </div>
            </Modal>
        );
    }
}
